import { useLazyQuery, useQuery } from '@apollo/client';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapBoundsQuery,
  MapItemQuery,
  MapItemsQuery,
  MapMarkersQuery,
  MapMarkersWithTotalQuery
} from '../../../graphql/graphql';
import {
  mapBoundsQuery,
  mapItemQuery,
  mapItemsQuery,
  mapMarkersQuery,
  mapMarkersWithTotalQuery
} from '../../../api/queries';
import {
  getShrunkBoundsFromMap,
  isPointInBackendBounds,
  reconcileMarkers
} from '../../../utils/helpers/map';
import { Marker } from '../../ItemsMap/types';
import { ItemsMap } from '../../ItemsMap';
import { ManatIcon } from '../NewSearchBar/PriceFilter/styled';
import { CurrencySymbol } from '../../ui/ItemCard/CurrencySymbol';
import { PriceValue, SearchResultMapContainer } from './styled';
import { prettyNumber } from '../../../utils/helpers';
import { ItemsList } from './ItemsList';

interface SearchResultsItemsMapProps {
  title: string;
  filter: {
    [key: string]: string | boolean;
  };
  onTotalCountChange: (count: number) => void;
}

const units = (
  <ManatIcon className="manat-icon">
    <CurrencySymbol currency="AZN" />
  </ManatIcon>
);

const getItemLabel = (
  price: number,
  isLeased: boolean,
  leasedSuffix: string | boolean
) => (
  <PriceValue>
    {prettyNumber(price)}
    {units}
    {isLeased && `/${leasedSuffix}`}
  </PriceValue>
);

export const SearchResultsItemsMap: React.FC<SearchResultsItemsMapProps> = memo(
  ({ title, filter, onTotalCountChange }) => {
    const { t } = useTranslation();
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [zoom, setZoom] = useState<number | undefined>(undefined);

    const [activeSelection, setActiveSelection] = useState<Marker | null>(null);
    const [hasLoadedTotal, setHasLoadedTotal] = useState(false);

    const [displayMarkers, setDisplayMarkers] = useState<Marker[]>([]);

    const { data: mapBoundsData } = useQuery<MapBoundsQuery>(mapBoundsQuery, {
      variables: { filter }
    });

    const isInitialLoad = !hasLoadedTotal;

    const { data: mapMarkersData, loading: markersLoading } = useQuery<
      MapMarkersQuery | MapMarkersWithTotalQuery
    >(isInitialLoad ? mapMarkersWithTotalQuery : mapMarkersQuery, {
      skip: !map || zoom === undefined,
      variables: {
        filter: {
          ...filter,
          bounds: map ? getShrunkBoundsFromMap(map) : null
        },
        zoom,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
      },
      onCompleted: data => {
        const totalCount = (data as MapMarkersWithTotalQuery)?.mapMarkers
          ?.totalCount;
        if (isInitialLoad && totalCount !== undefined) {
          onTotalCountChange(totalCount);
          setHasLoadedTotal(true);
        }
      }
    });

    useEffect(() => {
      setHasLoadedTotal(false);
    }, [filter]);

    const {
      data: clusterItemsData,
      loading,
      fetchMore
    } = useQuery<MapItemsQuery>(mapItemsQuery, {
      variables: {
        first: 4,
        filter: {
          ...filter,
          bounds: map ? getShrunkBoundsFromMap(map) : null,
          mapClusterId: activeSelection?.id
        }
      },
      skip: !activeSelection || activeSelection.type !== 'MapCluster' || !map,
      notifyOnNetworkStatusChange: true
    });

    const [
      loadSingleItem,
      { data: singleItemData, loading: singleItemLoading }
    ] = useLazyQuery<MapItemQuery>(mapItemQuery);

    useEffect(() => {
      if (!activeSelection || !map) return;

      const isCluster = activeSelection.type === 'MapCluster';

      if (!isCluster) {
        loadSingleItem({
          variables: {
            id: activeSelection.id
          }
        });
      }
    }, [activeSelection, map, loadSingleItem]);

    const clusterItems = useMemo(
      () => clusterItemsData?.mapItems?.edges?.map(e => e?.node) ?? [],
      [clusterItemsData]
    );

    const singleItem = useMemo(
      () => (singleItemData?.item ? [singleItemData.item] : []),
      [singleItemData]
    );

    const items = useMemo(() => {
      if (!activeSelection) return [];
      return activeSelection.type === 'MapCluster' ? clusterItems : singleItem;
    }, [activeSelection, clusterItems, singleItem]);

    const preparedMarkers = useMemo(() => {
      const markers = mapMarkersData?.mapMarkers.markers;
      const backendBounds = mapMarkersData?.mapMarkers.bounds;

      if (!markers || !backendBounds) return [];

      return markers
        .filter(marker =>
          isPointInBackendBounds(marker.lat, marker.lng, backendBounds)
        )
        .map(marker => {
          // eslint-disable-next-line no-underscore-dangle
          const isClusterMarker = marker.__typename === 'MapCluster';

          const leasedSuffix =
            !isClusterMarker &&
            t(
              marker.isPaidDaily
                ? 'new_search.leased_daily'
                : 'new_search.leased_monthly'
            );

          return {
            id: isClusterMarker ? marker.id : marker.itemId,
            // eslint-disable-next-line no-underscore-dangle
            type: marker.__typename,
            position: { lat: marker.lat, lng: marker.lng },
            label: isClusterMarker
              ? marker.count
              : getItemLabel(marker.price, marker.isLeased, leasedSuffix)
          };
        });
    }, [mapMarkersData, t]);

    useEffect(() => {
      if (!preparedMarkers.length && markersLoading) return;
      setDisplayMarkers(prev => reconcileMarkers(prev, preparedMarkers));
    }, [preparedMarkers, markersLoading]);

    const handleLoadMore = () => {
      const pageInfo = clusterItemsData?.mapItems?.pageInfo;
      if (!pageInfo?.hasNextPage || loading) return;

      fetchMore({
        variables: {
          first: 4,
          after: pageInfo.endCursor,
          filter: {
            ...filter,
            bounds: map ? getShrunkBoundsFromMap(map) : null,
            mapClusterId: activeSelection?.id
          }
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newItems = fetchMoreResult?.mapItems;

          if (!newItems) {
            return previousResult;
          }

          return {
            ...previousResult,
            mapItems: {
              ...previousResult.mapItems,
              pageInfo: newItems.pageInfo,
              edges: [
                ...(previousResult.mapItems?.edges ?? []),
                ...(newItems.edges ?? [])
              ]
            }
          };
        }
      });
    };

    return (
      <SearchResultMapContainer $markersLoading={markersLoading}>
        <ItemsMap
          title={title}
          zoom={zoom}
          map={map}
          mapBounds={mapBoundsData?.mapBounds}
          mapMarkers={displayMarkers}
          activeSelection={activeSelection}
          setMap={setMap}
          setZoom={setZoom}
          setActiveSelection={setActiveSelection}
        >
          {activeSelection && (
            <ItemsList
              key={activeSelection.id}
              items={items}
              loading={loading || singleItemLoading}
              hasNextPage={clusterItemsData?.mapItems?.pageInfo?.hasNextPage}
              onLoadMore={handleLoadMore}
              isClusterMarker={activeSelection.type === 'MapCluster'}
            />
          )}
        </ItemsMap>
      </SearchResultMapContainer>
    );
  }
);

SearchResultsItemsMap.displayName = 'SearchResultsItemsMap';
